import React, { useState, useCallback } from 'react';

import { Button, Spinner } from '@ergeon/core-components';
import { ChatBubbleLeftRightIcon, TrashIcon } from '@ergeon/icons/hero/micro';
import clsx from 'classnames';

import { formatRelativeTime } from '../Customers/utils/formatUtils';
import type { AIAgentThread } from '../../types';

import DeleteThreadDialog from './DeleteThreadDialog';

type AIAgentThreadItemProps = {
  thread: AIAgentThread;
  isSelected: boolean;
  onSelect: (threadId: number) => void;
  onDelete: (threadId: number) => Promise<void>;
};

const getContainerStateClasses = (isSelected: boolean) => {
  return clsx(
    'group flex items-center gap-2 p-4 rounded-md outline outline-1 -outline-offset-1 transition-all duration-100 cursor-pointer',
    {
      'bg-blue-100 dark:bg-blue-900 outline-blue-200 dark:outline-blue-600': isSelected,
      'hover:bg-gray-100 dark:hover:bg-gray-700 outline-transparent': !isSelected,
    },
  );
};

const getIconStateClasses = (isSelected: boolean) => {
  return clsx('size-5 shrink-0', {
    'text-blue-500 dark:text-blue-600': isSelected,
    'text-gray-400 dark:text-gray-600': !isSelected,
  });
};

const AIAgentThreadItem: React.FC<AIAgentThreadItemProps> = ({
  thread,
  isSelected,
  onSelect,
  onDelete,
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleClick = () => {
    onSelect(thread.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSelect(thread.id);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(async() => {
    await onDelete(thread.id);
    setIsDeleteDialogOpen(false);
  }, [onDelete, thread.id]);

  return (
    <div
      className={getContainerStateClasses(isSelected)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}>
      <ChatBubbleLeftRightIcon className={getIconStateClasses(isSelected)} />

      <div className="flex justify-between items-center gap-2 w-full min-w-0">
        <div className="flex flex-col flex-1 min-w-0">
          <p className="font-bold text-gray-900 dark:text-white truncate paragraph-2">
            {thread.preview || 'New conversation'}
          </p>
          <p className="text-gray-600 dark:text-gray-400 paragraph-3">
            {formatRelativeTime(thread.modified_at)}
          </p>
        </div>

        <div className="flex items-center shrink-0">
          {thread.is_processing && (
            <Spinner
              active
              borderWidth="0.125"
              className="group-hover:hidden"
              color="lightgray"
              size={24} />
          )}
          <Button
            className={thread.is_processing ? 'hidden group-hover:flex' : 'opacity-0 group-hover:opacity-100'}
            flavor="danger"
            isSquare
            onClick={handleDeleteClick}
            size="small"
            taste="line"
            title="Delete conversation">
            <TrashIcon className="size-4" />
          </Button>
        </div>
      </div>

      <DeleteThreadDialog
        isVisible={isDeleteDialogOpen}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDelete}
        threadPreview={thread.preview} />
    </div>
  );
};

export default AIAgentThreadItem;
